import {getTypedEntries} from "@/core/util/getTypedEntries";
import {BasketRule, OrderAttributesValue} from "@/gql/gen";
import isEqual from "lodash-es/isEqual";
import {toRaw} from "vue";
import {ProductModel} from "@/modules/shop/composable/useProduct";
import {ProductDiscountsCalculation, ProductDiscountsCalculationOptions} from "@/modules/shop/store/util/benefit";

export type VorderTab = 'basket' | 'form'

export type OrderAttributesChanged = {
    [key in keyof OrderAttributesValue]?: {
        value: OrderAttributesValue[key];
        prevValue: OrderAttributesValue[key];
    };
}

export type OrderAttributesChangedValues = {
    [key in keyof OrderAttributesValue]?: OrderAttributesValue[key];
}

export function canDeliverForAttrs(attrs: OrderAttributesValue) {
    if (attrs.TRANSPORT_TYPE === 'courier') {
        return !!(attrs.ADDRESS)
    }
    if (attrs.TRANSPORT_TYPE === 'pickup') {
        return !!(attrs.PICKUP_DEPARTMENT)
    }
    return false
}


export function getDeliveryPriceForAttrs(attrs: OrderAttributesValue) {
    return calcDeliveryPrice(attrs)
}

export function compareAttrs(prevAttrs: OrderAttributesValue, newAttrs: Partial<OrderAttributesValue>) {
    let changed: OrderAttributesChanged = {}
    const changedValues: OrderAttributesChangedValues = {};

    for (let [attrName, attrValue] of getTypedEntries(newAttrs)) {
        const prevValue = toRaw(prevAttrs[attrName])
        attrValue = toRaw(attrValue);

        if (
            typeof attrValue !== "boolean" &&
            typeof prevValue !== "boolean" &&
            !attrValue &&
            !prevValue
        )
            continue;

        if (!isEqual(attrValue, prevValue)) {
            changed[attrName] = {
                value: attrValue,
                prevValue: prevValue,
            };
            changedValues[attrName] = attrValue;
        }
    }

    return {
        isChanged: !!Object.values(changed).length,
        changed,
        changedValues
    }
}

// ============================================================================
// BASKET RULES DISCOUNT CALCULATION
// ============================================================================

export type DiscountBenefitWithRule = {
    __typename: 'BasketRuleBenefitDiscount';
    DISCOUNT_PERCENT: number | null;
    rule: BasketRule;
    ruleId: number;
};

export type BasketRuleState = {
    rule: BasketRule;
    baseActive: boolean;
    active: boolean;
};

/**
 * Рассчитывает скидки для товара на основе активных правил скидок
 *
 * Интеграция с существующей системой скидок:
 * - Использует те же интерфейсы (ProductDiscountsCalculation)
 * - Применяет ту же логику фильтрации по товарам/секциям
 * - Совместим с существующим API calculateProductDiscounts
 *
 * Requirements: 5.1, 5.2, 5.3, 7.3
 *
 * @param product - Товар для расчета скидок
 * @param discountStates - Активные состояния правил скидок
 * @param options - Опции расчета (basePrice для элементов корзины)
 * @returns Расчет скидок с ценами
 */
export function calculateProductDiscountsUtil(
    product: ProductModel,
    discountStates: BasketRuleState[],
    options: ProductDiscountsCalculationOptions = {}
): ProductDiscountsCalculation {
    // Фильтруем скидки, применимые к конкретному товару (с информацией о правиле)
    const applicableDiscounts: DiscountBenefitWithRule[] = [];

    for (const state of discountStates) {
        const rule = state.rule;
        if (!rule.BENEFITS) continue;

        // Проверяем условия правила по товарам
        const condition = rule.CONDITION;
        let isApplicable = false;

        // Если нет ограничений по товарам - применяется ко всем
        if (condition.PRODUCT.length === 0 && condition.PRODUCT_SECTION.length === 0) {
            isApplicable = true;
        } else {
            // Проверка по ID товара
            if (condition.PRODUCT.includes(product.ID)) {
                isApplicable = true;
            }

            // Проверка по разделам товара
            if (!isApplicable && product.IBLOCK_SECTION_IDS) {
                const hasMatchingSection = product.IBLOCK_SECTION_IDS.some(sectionId =>
                    condition.PRODUCT_SECTION.includes(sectionId)
                );
                if (hasMatchingSection) {
                    isApplicable = true;
                }
            }
        }

        if (isApplicable) {
            // Добавляем все скидки из правила с информацией о правиле
            for (const benefit of rule.BENEFITS) {
                if (benefit.__typename === 'BasketRuleBenefitDiscount') {
                    applicableDiscounts.push({
                        __typename: 'BasketRuleBenefitDiscount',
                        DISCOUNT_PERCENT: benefit.DISCOUNT_PERCENT,
                        rule,
                        ruleId: rule.ID,
                    });
                }
            }
        }
    }

    // Выбираем максимальную скидку
    const maxDiscount = applicableDiscounts.length > 0
        ? applicableDiscounts.reduce((max, current) => {
            const maxPercent = max.DISCOUNT_PERCENT || 0;
            const currentPercent = current.DISCOUNT_PERCENT || 0;
            return currentPercent > maxPercent ? current : max;
        })
        : null;

    // Используем basePrice из опций (для корзины) или getPriceSource (для каталога)
    let price = options.basePrice ?? product.getPriceSource();
    let priceSource = price;
    let discountPercent = 0;
    let appliedRule: BasketRule | undefined;
    let appliedRuleId: number | undefined;

    // Применяем формулу расчёта цены со скидкой
    if (maxDiscount && maxDiscount.DISCOUNT_PERCENT) {
        // Ограничиваем максимум 100%, игнорируем отрицательные значения
        discountPercent = Math.max(0, Math.min(100, maxDiscount.DISCOUNT_PERCENT));

        if (discountPercent > 0) {
            price = price * (1 - discountPercent / 100);
            appliedRule = maxDiscount.rule;
            appliedRuleId = maxDiscount.ruleId;
        }
    }

    return {
        discountPercent,
        priceSource,
        price,
        appliedRule,
        appliedRuleId,
    };
}

/**
 * Рассчитывает скидки для товара в каталоге на основе базово активных правил
 *
 * Использует baseActiveDiscountRulesStates (без учета PRODUCT условия)
 * для показа скидок в каталоге до добавления товара в корзину
 *
 * Requirements: 5.1, 5.3, 7.3
 *
 * @param product - Товар для расчета скидок
 * @param discountStates - Базово активные состояния правил скидок
 * @returns Расчет скидок с ценами
 */
export function calculateProductDiscountsForCatalogUtil(
    product: ProductModel,
    discountStates: BasketRuleState[]
): ProductDiscountsCalculation {
    // Фильтруем скидки, применимые к конкретному товару
    const applicableDiscounts: DiscountBenefitWithRule[] = [];

    for (const state of discountStates) {
        const rule = state.rule;
        if (!rule.BENEFITS) continue;

        // Для каталога проверяем только PRODUCT_SECTION (PRODUCT уже отфильтрован в baseActive)
        const condition = rule.CONDITION;
        let isApplicable = false;

        // Если нет ограничений по разделам - применяется ко всем
        if (condition.PRODUCT_SECTION.length === 0) {
            isApplicable = true;
        } else {
            // Проверка по разделам товара
            if (product.IBLOCK_SECTION_IDS) {
                const hasMatchingSection = product.IBLOCK_SECTION_IDS.some(sectionId =>
                    condition.PRODUCT_SECTION.includes(sectionId)
                );
                if (hasMatchingSection) {
                    isApplicable = true;
                }
            }
        }

        if (isApplicable) {
            // Добавляем все скидки из правила с информацией о правиле
            for (const benefit of rule.BENEFITS) {
                if (benefit.TYPE === 'discount' && benefit.__typename === 'BasketRuleBenefitDiscount') {
                    applicableDiscounts.push({
                        __typename: 'BasketRuleBenefitDiscount',
                        DISCOUNT_PERCENT: benefit.DISCOUNT_PERCENT,
                        rule,
                        ruleId: rule.ID,
                    });
                }
            }
        }
    }

    // Выбираем максимальную скидку
    const maxDiscount = applicableDiscounts.length > 0
        ? applicableDiscounts.reduce((max, current) => {
            const maxPercent = max.DISCOUNT_PERCENT || 0;
            const currentPercent = current.DISCOUNT_PERCENT || 0;
            return currentPercent > maxPercent ? current : max;
        })
        : null;

    let price = product.getPriceSource();
    let priceSource = price;
    let discountPercent = 0;
    let appliedRule: BasketRule | undefined;
    let appliedRuleId: number | undefined;

    // Применяем формулу расчёта цены со скидкой
    if (maxDiscount && maxDiscount.DISCOUNT_PERCENT) {
        discountPercent = Math.max(0, Math.min(100, maxDiscount.DISCOUNT_PERCENT));

        if (discountPercent > 0) {
            price = price * (1 - discountPercent / 100);
            price = Math.round(price)
            appliedRule = maxDiscount.rule;
            appliedRuleId = maxDiscount.ruleId;
        }
    }

    return {
        discountPercent,
        priceSource,
        price,
        appliedRule,
        appliedRuleId,
    };
}
